/**
 * landingV3.content.js — copy de las páginas públicas del rediseño V3.
 *
 * Mismo criterio que `landingV2.content.js`: para páginas de marketing con
 * mucha prosa preferimos un módulo de contenido por idioma a cientos de claves
 * planas en translation.json. El JSX queda legible (`c.nav.producto[0].titulo`)
 * y traducir es rellenar otro objeto de idioma aquí.
 *
 * QUÉ VA AQUÍ Y QUÉ NO. Aquí vive la PROSA: titulares, párrafos, descripciones
 * de los desplegables, FAQs. Las etiquetas cortas del chrome (Entrar, Menú, los
 * enlaces legales del pie) siguen en `src/locales/<lang>/translation.json`, porque
 * los tests de paridad de `src/locales/__tests__` las vigilan en los cinco
 * idiomas y este módulo no tiene esa red.
 *
 * Los idiomas distintos de `es` se rellenan en su propio PR; hasta entonces
 * `pickLandingContent` cae a español, que es lo que ya hace la V2 para un
 * idioma incompleto.
 *
 * El copy de cada página grande vive en su propio fichero y se cuelga aquí. La
 * home sola tiene más prosa que el resto de la V3 junta: con las once dentro,
 * esto sería otro `landingV2.content.js` de 3.374 líneas.
 */
import { homeV3 } from './landingV3.home';
import { productoV3 } from './landingV3.producto';
import { multiubicacionesV3 } from './landingV3.multiubicaciones';
import { sectoresV3 } from './landingV3.sectores';
import { sobreNosotrosV3 } from './landingV3.sobreNosotros';

/**
 * Los ocho destinos de los desplegables de la cabecera.
 *
 * **Aquí no hay URLs.** `clave` es la de `lib/rutasPublicas.js` y `LandingNavV3`
 * saca el destino del idioma que se esté sirviendo. Antes cada idioma llevaba
 * su `to` escrito a mano, y los cinco apuntaban a la ruta española: el
 * desplegable de la página alemana te sacaba a una página en español.
 */
const es = {
  nav: {
    producto: {
      etiqueta: 'Producto',
      items: [
        {
          clave: 'visibilidadSeo',
          titulo: 'Visibilidad SEO',
          desc: 'El puesto en el que sales en las búsquedas y cómo subir tu posición',
        },
        {
          clave: 'automatizaciones',
          titulo: 'Automatizaciones IA',
          desc: 'Respuestas automáticas a cada reseña con el tono de tu marca',
        },
        {
          clave: 'publicacionesIa',
          titulo: 'Publicaciones IA',
          desc: 'Ahorra tiempo creando publicaciones y prográmalas',
        },
        {
          clave: 'widgetResenas',
          titulo: 'Widget de reseñas',
          desc: 'Tus reseñas de Google, luciendo en tu propia web',
        },
      ],
    },
    soluciones: {
      etiqueta: 'Soluciones',
      items: [
        {
          clave: 'restauracion',
          titulo: 'Restauración',
          desc: 'Cada uno de tus restaurantes compitiendo por ser el nº1',
        },
        {
          clave: 'clinicas',
          titulo: 'Clínicas',
          desc: 'Reseñas respondidas con la prudencia que la salud pide',
        },
        {
          clave: 'automotriz',
          titulo: 'Automotriz',
          desc: 'La confianza que decide antes de dejar el coche',
        },
        {
          clave: 'hoteles',
          titulo: 'Hoteles',
          desc: 'Reseñas en el idioma de cada huésped, aunque lleguen a la vez',
        },
      ],
    },
    multiubicaciones: 'Multiubicaciones',
    precios: "Precios",
    sobreNosotros: 'Sobre nosotros',
  },

  /*
   * El formulario de captación. Vive en el tronco común y no en el copy de una
   * página porque el componente es uno solo: hoy lo monta Multiubicaciones y
   * mañana puede montarlo cualquier otra, y el texto no tiene por qué cambiar.
   */
  contacto: {
    titulo: 'Solicita más información, ',
    tituloAcento: 'sin compromiso',
    cuerpo:
      'Cuéntanos sobre tu grupo y te explicamos cómo encaja con tus locales. Te contactamos en menos de 24 horas.',

    nombre: 'Nombre',
    apellidos: 'Apellidos',
    telefono: 'Teléfono',
    email: 'Email de empresa',
    sedes: '¿Cuántas sedes tienes?',
    mensaje: 'Mensaje',
    mensajePista: 'Cuéntanos qué necesitas: cuántos locales tienes, qué te gustaría resolver…',

    // El checkbox de consentimiento. Partido en tres para que el enlace quede
    // DENTRO de la frase y no colgando al final: «acepto la [política]» se lee
    // como una frase; «acepto los términos. Política de privacidad» no.
    politicaA: 'He leído y acepto la ',
    politicaEnlace: 'política de privacidad',
    politicaB: '.',

    enviar: 'Solicitar información',
    enviando: 'Enviando…',
    pie: 'Te contactamos en menos de 24 horas',

    okTitulo: '¡Recibido!',
    okCuerpo: 'Te escribimos en menos de 24 horas. Si es urgente, llámanos al 604 934 869.',

    // Los errores se dicen en cristiano y al lado del campo. Nada de «campo
    // obligatorio» genérico: quien rellena un formulario de una landing no
    // tiene ninguna paciencia para adivinar cuál falta.
    errNombre: 'Dinos cómo te llamas',
    errEmail: 'Necesitamos un email válido para responderte',
    errPolitica: 'Necesitamos tu permiso para guardar estos datos y responderte',
    errEnvio: 'No hemos podido enviarlo. Inténtalo otra vez o llámanos al 604 934 869.',
    errThrottle: 'Ya hemos recibido tu mensaje. Si es urgente, llámanos al 604 934 869.',
  },

  precios: {
    titulo: "Un plan, todo incluido",
    subtitulo: "Sin módulos ni sorpresas. Empieza con 7 días de prueba.",


    /*
     * El FAQ de la página de precios (no sale en la home).
     *
     * Es lo que hace que `/pricing` tenga algo que indexar: los importes
     * vienen de `/billing/active-pricing` y en el HTML estático la tabla sale
     * vacía, así que sin esto la página serían 250 palabras y ni un número.
     * Aquí son ~400 más, y además le dan el `FAQPage` de schema.org.
     *
     * Lo que afirma está comprobado contra lo que ya dice la app —el
     * prorrateo, la ausencia de permanencia, la tarjeta en el trial, el tope
     * de 10 respuestas— y no inventado para rellenar.
     */
    faqTitulo: "Preguntas sobre precios",
    faq: [
      {
        q: "¿Qué incluye el plan gratuito?",
        a: "Conectar tu ficha de Google Business, para siempre y sin suscripción ni tarjeta: todas tus reseñas en una bandeja, el Mapa SEO y el análisis de tu ficha. Puedes responder a 10 reseñas al mes con ayuda de la IA.",
      },
      {
        q: "¿Business se paga por cuenta o por local?",
        a: "Por local activo. Puedes tener varios locales en la misma cuenta y activar solo los que quieras; el selector de la tabla te enseña el total antes de contratar nada.",
      },
      {
        q: "¿Qué pasa si añado o quito un local a mitad de mes?",
        a: "El cobro se ajusta solo: se te abona la parte no usada y se carga el nuevo total prorrateado. No hay que esperar a la renovación ni rehacer la suscripción.",
      },
      {
        q: "¿Hay permanencia?",
        a: "No. Es una suscripción mensual o anual que puedes cancelar cuando quieras. Al cancelar conservas Business hasta el final del periodo que ya has pagado.",
      },
      {
        q: "¿La prueba gratuita pide tarjeta?",
        a: "Sí, para que al terminar la prueba el servicio no se corte. Durante el periodo de prueba no se cobra nada, y si cancelas antes de que acabe no hay ningún cargo.",
      },
      {
        q: "¿Puedo pasar de mensual a anual?",
        a: "Sí, cuando quieras. El cambio se aplica al instante y el importe prorrateado del periodo en curso se cobra en ese momento.",
      },
    ],
    gratis: {
      nombre: 'Gratis',
      insignia: 'Sin tarjeta',
      tagline: 'Conecta tu ficha de Google Business',
      precio: '0 €',
      nota: 'Para siempre, sin suscripción',
      cta: 'Empezar gratis',
      // Lo que da conectar la ficha de Google, SIN dispositivos: aún no está
      // decidido cómo entran los dispositivos en la landing, así que aquí no se
      // nombran (vinculación, escaneos y ranking del personal son de hardware).
      //
      // ⚠️ «10 respuestas … AL MES» es la redacción fiel:
      // `FREE_MANUAL_REPLY_QUOTA_PER_MONTH` vale 10 y es mensual, no un saldo
      // que se gasta una vez. El otro tope del plan gratis son 20 generaciones
      // con IA al mes (`FREE_AUTO_REPLY_QUOTA_PER_MONTH`); el «10» es el de
      // publicar en Google, que es el más estrecho de los dos y el que de verdad
      // frena.
      //
      // ⚠️ El mapa SEO en gratis depende de `GEOGRID_CREDITS_ENABLED`. Con el
      // flag apagado (que es el valor por defecto) escanear vuelve a exigir
      // Pro y este bullet sería mentira. Hay que confirmarlo en producción
      // antes de desplegar.
      features: [
        'Todas tus reseñas de Google en una bandeja',
        '10 respuestas a reseñas con IA al mes',
        'Mapa SEO: tu posición en Google Maps punto por punto',
        'Análisis SEO de tu ficha, con puntuación y plan de acción',
      ],
    },

    enterprise: {
      nombre: 'Enterprise',
      insignia: 'Franquicias',
      tagline: 'Más de 30 locales',
      precio: 'A medida',
      nota: 'Según número de locales',
      cta: 'Contacta con ventas',
      features: [
        'Todo Business en todos los locales',
        'Vista de conjunto y por sitio',
        'Informes agregados para dirección',
        'Onboarding y soporte dedicado',
      ],
    },
  },
};

/*
 * Las traducciones del tronco. Las reglas —qué se traduce, qué no y con qué
 * trato en cada idioma— están escritas una sola vez, en `landingV3.home.js`.
 *
 * Aquí solo un detalle que se pierde si no se dice: **los items de los
 * desplegables ya no llevan URL.** Solo `clave`, que sí es idéntica en los cinco
 * idiomas porque es un identificador y no texto; el destino lo resuelve
 * `LandingNavV3` contra `lib/rutasPublicas.js` según el idioma. El test de
 * paridad comprueba que las claves no se traduzcan.
 */
const en = {
  nav: {
    producto: {
      etiqueta: 'Product',
      items: [
        {
          clave: 'visibilidadSeo',
          titulo: 'SEO visibility',
          desc: 'The position you come up in on searches and how to climb',
        },
        {
          clave: 'automatizaciones',
          titulo: 'AI automations',
          desc: 'Automatic replies to every review in your brand’s tone',
        },
        {
          clave: 'publicacionesIa',
          titulo: 'AI posts',
          desc: 'Save time creating posts and schedule them',
        },
        {
          clave: 'widgetResenas',
          titulo: 'Reviews widget',
          desc: 'Your Google reviews, on show on your own website',
        },
      ],
    },
    soluciones: {
      etiqueta: 'Solutions',
      items: [
        {
          clave: 'restauracion',
          titulo: 'Restaurants',
          desc: 'Every one of your restaurants competing to be no. 1',
        },
        {
          clave: 'clinicas',
          titulo: 'Clinics',
          desc: 'Reviews answered with the care that healthcare calls for',
        },
        {
          clave: 'automotriz',
          titulo: 'Automotive',
          desc: 'The trust that decides before they leave the car with you',
        },
        {
          clave: 'hoteles',
          titulo: 'Hotels',
          desc: 'Reviews in each guest’s language, even when they all arrive at once',
        },
      ],
    },
    multiubicaciones: 'Multi-location',
    precios: "Pricing",
    sobreNosotros: 'About us',
  },

  contacto: {
    titulo: 'Request more information, ',
    tituloAcento: 'no strings attached',
    cuerpo:
      'Tell us about your group and we will explain how it fits your locations. We get in touch within 24 hours.',

    nombre: 'First name',
    apellidos: 'Surname',
    telefono: 'Phone',
    email: 'Work email',
    sedes: 'How many locations do you have?',
    mensaje: 'Message',
    mensajePista: 'Tell us what you need: how many locations you have, what you would like to solve…',

    politicaA: 'I have read and accept the ',
    politicaEnlace: 'privacy policy',
    politicaB: '.',

    enviar: 'Request information',
    enviando: 'Sending…',
    pie: 'We get in touch within 24 hours',

    okTitulo: 'Got it!',
    okCuerpo: 'We will write to you within 24 hours. If it is urgent, call us on 604 934 869.',

    errNombre: 'Tell us your name',
    errEmail: 'We need a valid email to get back to you',
    errPolitica: 'We need your permission to store these details and reply',
    errEnvio: 'We could not send it. Try again or call us on 604 934 869.',
    errThrottle: 'We have already received your message. If it is urgent, call us on 604 934 869.',
  },

  precios: {
    titulo: "One plan, everything included",
    subtitulo: "No modules, no surprises. Start with a 7-day free trial.",


    /*
     * El FAQ de la página de precios (no sale en la home).
     *
     * Es lo que hace que `/pricing` tenga algo que indexar: los importes
     * vienen de `/billing/active-pricing` y en el HTML estático la tabla sale
     * vacía, así que sin esto la página serían 250 palabras y ni un número.
     * Aquí son ~400 más, y además le dan el `FAQPage` de schema.org.
     *
     * Lo que afirma está comprobado contra lo que ya dice la app —el
     * prorrateo, la ausencia de permanencia, la tarjeta en el trial, el tope
     * de 10 respuestas— y no inventado para rellenar.
     */
    faqTitulo: "Pricing questions",
    faq: [
      {
        q: "What does the free plan include?",
        a: "Connecting your Google Business Profile, forever and with no subscription or card: all your reviews in one inbox, the SEO Map and the analysis of your listing. You can reply to 10 reviews a month with help from AI.",
      },
      {
        q: "Is Business billed per account or per location?",
        a: "Per active location. You can keep several locations in one account and activate only the ones you want; the selector in the table shows you the total before you commit to anything.",
      },
      {
        q: "What if I add or remove a location mid-month?",
        a: "Billing adjusts itself: you are credited for the unused part and charged the new prorated total. No waiting for the renewal and no redoing the subscription.",
      },
      {
        q: "Is there a minimum term?",
        a: "No. It is a monthly or annual subscription you can cancel whenever you want. When you cancel you keep Business until the end of the period you already paid for.",
      },
      {
        q: "Does the free trial ask for a card?",
        a: "Yes, so the service does not cut off when the trial ends. Nothing is charged during the trial, and if you cancel before it finishes there is no charge at all.",
      },
      {
        q: "Can I switch from monthly to annual?",
        a: "Yes, whenever you want. The change applies immediately and the prorated amount for the current period is charged right away.",
      },
    ],
    gratis: {
      nombre: 'Free',
      insignia: 'No card',
      tagline: 'Connect your Google Business Profile',
      precio: '€0',
      nota: 'Forever, no subscription',
      cta: 'Start for free',
      features: [
        'All your Google reviews in one inbox',
        '10 AI review replies a month',
        'SEO map: your position on Google Maps point by point',
        'SEO analysis of your listing, with a score and an action plan',
      ],
    },

    enterprise: {
      nombre: 'Enterprise',
      insignia: 'Franchises',
      tagline: 'More than 30 locations',
      precio: 'Custom',
      nota: 'Based on number of locations',
      cta: 'Contact sales',
      features: [
        'Everything in Business, at every location',
        'Group-wide and per-site view',
        'Aggregated reports for management',
        'Dedicated onboarding and support',
      ],
    },
  },
};

const pt = {
  nav: {
    producto: {
      etiqueta: 'Produto',
      items: [
        {
          clave: 'visibilidadSeo',
          titulo: 'Visibilidade SEO',
          desc: 'A posição em que aparece nas pesquisas e como subir',
        },
        {
          clave: 'automatizaciones',
          titulo: 'Automatizações com IA',
          desc: 'Respostas automáticas a cada avaliação com o tom da sua marca',
        },
        {
          clave: 'publicacionesIa',
          titulo: 'Publicações com IA',
          desc: 'Poupe tempo a criar publicações e agende-as',
        },
        {
          clave: 'widgetResenas',
          titulo: 'Widget de avaliações',
          desc: 'As suas avaliações do Google, em destaque no seu site',
        },
      ],
    },
    soluciones: {
      etiqueta: 'Soluções',
      items: [
        {
          clave: 'restauracion',
          titulo: 'Restauração',
          desc: 'Cada um dos seus restaurantes a competir pelo nº 1',
        },
        {
          clave: 'clinicas',
          titulo: 'Clínicas',
          desc: 'Avaliações respondidas com a prudência que a saúde exige',
        },
        {
          clave: 'automotriz',
          titulo: 'Automóvel',
          desc: 'A confiança que decide antes de deixar o carro',
        },
        {
          clave: 'hoteles',
          titulo: 'Hotéis',
          desc: 'Avaliações no idioma de cada hóspede, mesmo que cheguem ao mesmo tempo',
        },
      ],
    },
    multiubicaciones: 'Multilocalização',
    precios: "Preços",
    sobreNosotros: 'Sobre nós',
  },

  contacto: {
    titulo: 'Peça mais informações, ',
    tituloAcento: 'sem compromisso',
    cuerpo:
      'Conte-nos sobre o seu grupo e explicamos-lhe como encaixa nos seus locais. Entramos em contacto em menos de 24 horas.',

    nombre: 'Nome',
    apellidos: 'Apelidos',
    telefono: 'Telefone',
    email: 'Email de empresa',
    sedes: 'Quantos locais tem?',
    mensaje: 'Mensagem',
    mensajePista: 'Conte-nos o que precisa: quantos locais tem, o que gostaria de resolver…',

    politicaA: 'Li e aceito a ',
    politicaEnlace: 'política de privacidade',
    politicaB: '.',

    enviar: 'Pedir informações',
    enviando: 'A enviar…',
    pie: 'Entramos em contacto em menos de 24 horas',

    okTitulo: 'Recebido!',
    okCuerpo: 'Escrevemos-lhe em menos de 24 horas. Se for urgente, ligue-nos para o 604 934 869.',

    errNombre: 'Diga-nos como se chama',
    errEmail: 'Precisamos de um email válido para lhe responder',
    errPolitica: 'Precisamos da sua autorização para guardar estes dados e responder-lhe',
    errEnvio: 'Não conseguimos enviar. Tente outra vez ou ligue-nos para o 604 934 869.',
    errThrottle: 'Já recebemos a sua mensagem. Se for urgente, ligue-nos para o 604 934 869.',
  },

  precios: {
    titulo: "Um plano, tudo incluído",
    subtitulo: "Sem módulos nem surpresas. Comece com 7 dias de teste.",


    /*
     * El FAQ de la página de precios (no sale en la home).
     *
     * Es lo que hace que `/pricing` tenga algo que indexar: los importes
     * vienen de `/billing/active-pricing` y en el HTML estático la tabla sale
     * vacía, así que sin esto la página serían 250 palabras y ni un número.
     * Aquí son ~400 más, y además le dan el `FAQPage` de schema.org.
     *
     * Lo que afirma está comprobado contra lo que ya dice la app —el
     * prorrateo, la ausencia de permanencia, la tarjeta en el trial, el tope
     * de 10 respuestas— y no inventado para rellenar.
     */
    faqTitulo: "Perguntas sobre preços",
    faq: [
      {
        q: "O que inclui o plano gratuito?",
        a: "Ligar a sua ficha do Google Business, para sempre e sem subscrição nem cartão: todas as suas avaliações numa caixa de entrada, o Mapa SEO e a análise da sua ficha. Pode responder a 10 avaliações por mês com a ajuda da IA.",
      },
      {
        q: "O Business paga-se por conta ou por estabelecimento?",
        a: "Por estabelecimento ativo. Pode ter vários estabelecimentos na mesma conta e ativar só os que quiser; o seletor da tabela mostra-lhe o total antes de contratar seja o que for.",
      },
      {
        q: "O que acontece se adicionar ou retirar um estabelecimento a meio do mês?",
        a: "A cobrança ajusta-se sozinha: é-lhe creditada a parte não utilizada e cobrado o novo total proporcional. Não é preciso esperar pela renovação nem refazer a subscrição.",
      },
      {
        q: "Há período de fidelização?",
        a: "Não. É uma subscrição mensal ou anual que pode cancelar quando quiser. Ao cancelar mantém o Business até ao fim do período que já pagou.",
      },
      {
        q: "O período experimental pede cartão?",
        a: "Sim, para que o serviço não se interrompa quando o período acabar. Durante o período experimental não é cobrado nada e, se cancelar antes de terminar, não há qualquer cobrança.",
      },
      {
        q: "Posso passar de mensal para anual?",
        a: "Sim, quando quiser. A alteração aplica-se de imediato e o valor proporcional do período em curso é cobrado no momento.",
      },
    ],
    gratis: {
      nombre: 'Grátis',
      insignia: 'Sem cartão',
      tagline: 'Ligue a sua ficha do Google Business',
      precio: '0 €',
      nota: 'Para sempre, sem subscrição',
      cta: 'Começar grátis',
      features: [
        'Todas as suas avaliações do Google numa caixa de entrada',
        '10 respostas a avaliações com IA por mês',
        'Mapa de SEO: a sua posição no Google Maps ponto a ponto',
        'Análise de SEO da sua ficha, com pontuação e plano de ação',
      ],
    },

    enterprise: {
      nombre: 'Enterprise',
      insignia: 'Franquias',
      tagline: 'Mais de 30 locais',
      precio: 'À medida',
      nota: 'Consoante o número de locais',
      cta: 'Fale com vendas',
      features: [
        'Tudo o do Business em todos os locais',
        'Vista de conjunto e por local',
        'Relatórios agregados para a direção',
        'Onboarding e apoio dedicados',
      ],
    },
  },
};

const de = {
  nav: {
    producto: {
      etiqueta: 'Produkt',
      items: [
        {
          clave: 'visibilidadSeo',
          titulo: 'SEO-Sichtbarkeit',
          desc: 'Die Position, auf der du in Suchen erscheinst, und wie du steigst',
        },
        {
          clave: 'automatizaciones',
          titulo: 'KI-Automatisierungen',
          desc: 'Automatische Antworten auf jede Bewertung im Ton deiner Marke',
        },
        {
          clave: 'publicacionesIa',
          titulo: 'KI-Beiträge',
          desc: 'Spare Zeit beim Erstellen von Beiträgen und plane sie ein',
        },
        {
          clave: 'widgetResenas',
          titulo: 'Bewertungs-Widget',
          desc: 'Deine Google-Bewertungen, sichtbar auf deiner eigenen Website',
        },
      ],
    },
    soluciones: {
      etiqueta: 'Lösungen',
      items: [
        {
          clave: 'restauracion',
          titulo: 'Gastronomie',
          desc: 'Jedes deiner Restaurants im Rennen um Platz 1',
        },
        {
          clave: 'clinicas',
          titulo: 'Praxen und Kliniken',
          desc: 'Bewertungen, beantwortet mit der Umsicht, die Gesundheit verlangt',
        },
        {
          clave: 'automotriz',
          titulo: 'Kfz-Branche',
          desc: 'Das Vertrauen, das entscheidet, bevor das Auto dableibt',
        },
        {
          clave: 'hoteles',
          titulo: 'Hotels',
          desc: 'Bewertungen in der Sprache jedes Gasts, auch wenn sie alle gleichzeitig kommen',
        },
      ],
    },
    multiubicaciones: 'Mehrere Standorte',
    precios: "Preise",
    sobreNosotros: 'Über uns',
  },

  contacto: {
    titulo: 'Fordere mehr Informationen an, ',
    tituloAcento: 'unverbindlich',
    cuerpo:
      'Erzähl uns von deiner Gruppe und wir erklären dir, wie das zu deinen Standorten passt. Wir melden uns in weniger als 24 Stunden.',

    nombre: 'Vorname',
    apellidos: 'Nachname',
    telefono: 'Telefon',
    email: 'Geschäftliche E-Mail',
    sedes: 'Wie viele Standorte hast du?',
    mensaje: 'Nachricht',
    mensajePista: 'Erzähl uns, was du brauchst: wie viele Standorte du hast, was du lösen möchtest…',

    // En alemán el verbo se va al final, así que la frase se reparte distinto
    // entre los tres trozos: «Ich habe die [política] gelesen und akzeptiere
    // sie.» Justo para esto está partida en tres y no concatenada.
    politicaA: 'Ich habe die ',
    politicaEnlace: 'Datenschutzerklärung',
    politicaB: ' gelesen und akzeptiere sie.',

    enviar: 'Informationen anfordern',
    enviando: 'Wird gesendet…',
    pie: 'Wir melden uns in weniger als 24 Stunden',

    okTitulo: 'Angekommen!',
    okCuerpo: 'Wir schreiben dir in weniger als 24 Stunden. Wenn es dringend ist, ruf uns an: 604 934 869.',

    errNombre: 'Sag uns, wie du heißt',
    errEmail: 'Wir brauchen eine gültige E-Mail, um dir zu antworten',
    errPolitica: 'Wir brauchen deine Erlaubnis, diese Daten zu speichern und dir zu antworten',
    errEnvio: 'Wir konnten es nicht senden. Versuch es noch einmal oder ruf uns an: 604 934 869.',
    errThrottle: 'Wir haben deine Nachricht schon erhalten. Wenn es dringend ist, ruf uns an: 604 934 869.',
  },

  precios: {
    titulo: "Ein Plan, alles inklusive",
    subtitulo: "Keine Module, keine Überraschungen. Starte mit 7 Tagen Testphase.",


    /*
     * El FAQ de la página de precios (no sale en la home).
     *
     * Es lo que hace que `/pricing` tenga algo que indexar: los importes
     * vienen de `/billing/active-pricing` y en el HTML estático la tabla sale
     * vacía, así que sin esto la página serían 250 palabras y ni un número.
     * Aquí son ~400 más, y además le dan el `FAQPage` de schema.org.
     *
     * Lo que afirma está comprobado contra lo que ya dice la app —el
     * prorrateo, la ausencia de permanencia, la tarjeta en el trial, el tope
     * de 10 respuestas— y no inventado para rellenar.
     */
    faqTitulo: "Fragen zu den Preisen",
    faq: [
      {
        q: "Was ist im kostenlosen Plan enthalten?",
        a: "Die Verbindung deines Google-Unternehmensprofils, für immer und ohne Abo oder Karte: alle deine Bewertungen in einem Posteingang, die SEO-Karte und die Analyse deines Eintrags. Mit Hilfe der KI kannst du auf 10 Bewertungen pro Monat antworten.",
      },
      {
        q: "Wird Business pro Konto oder pro Standort abgerechnet?",
        a: "Pro aktivem Standort. Du kannst mehrere Standorte in einem Konto haben und nur die aktivieren, die du willst; der Regler in der Tabelle zeigt dir die Summe, bevor du irgendetwas abschließt.",
      },
      {
        q: "Was passiert, wenn ich mitten im Monat einen Standort hinzufüge oder entferne?",
        a: "Die Abrechnung passt sich von selbst an: Der nicht genutzte Teil wird dir gutgeschrieben und die neue anteilige Summe berechnet. Kein Warten auf die Verlängerung und kein neues Abo.",
      },
      {
        q: "Gibt es eine Mindestlaufzeit?",
        a: "Nein. Es ist ein monatliches oder jährliches Abo, das du jederzeit kündigen kannst. Nach der Kündigung behältst du Business bis zum Ende des bereits bezahlten Zeitraums.",
      },
      {
        q: "Verlangt die kostenlose Testphase eine Karte?",
        a: "Ja, damit der Dienst am Ende der Testphase nicht abbricht. Während der Testphase wird nichts berechnet, und wenn du vorher kündigst, fällt keine Gebühr an.",
      },
      {
        q: "Kann ich von monatlich auf jährlich wechseln?",
        a: "Ja, jederzeit. Die Änderung gilt sofort, und der anteilige Betrag des laufenden Zeitraums wird direkt berechnet.",
      },
    ],
    gratis: {
      nombre: 'Kostenlos',
      insignia: 'Ohne Karte',
      tagline: 'Verbinde dein Google-Unternehmensprofil',
      precio: '0 €',
      nota: 'Für immer, ohne Abo',
      cta: 'Kostenlos starten',
      features: [
        'Alle deine Google-Bewertungen in einem Posteingang',
        '10 KI-Bewertungsantworten pro Monat',
        'SEO-Karte: deine Position bei Google Maps Punkt für Punkt',
        'SEO-Analyse deines Profils, mit Punktzahl und Maßnahmenplan',
      ],
    },

    enterprise: {
      nombre: 'Enterprise',
      insignia: 'Franchise',
      tagline: 'Mehr als 30 Standorte',
      precio: 'Individuell',
      nota: 'Je nach Anzahl der Standorte',
      cta: 'Vertrieb kontaktieren',
      features: [
        'Alles aus Business an allen Standorten',
        'Gesamtansicht und Ansicht pro Standort',
        'Aggregierte Berichte für die Geschäftsführung',
        'Eigenes Onboarding und eigener Support',
      ],
    },
  },
};

const fr = {
  nav: {
    producto: {
      etiqueta: 'Produit',
      items: [
        {
          clave: 'visibilidadSeo',
          titulo: 'Visibilité SEO',
          desc: 'La position à laquelle vous apparaissez dans les recherches et comment monter',
        },
        {
          clave: 'automatizaciones',
          titulo: 'Automatisations IA',
          desc: 'Réponses automatiques à chaque avis avec le ton de votre marque',
        },
        {
          clave: 'publicacionesIa',
          titulo: 'Publications IA',
          desc: 'Gagnez du temps en créant vos publications et programmez-les',
        },
        {
          clave: 'widgetResenas',
          titulo: 'Widget d’avis',
          desc: 'Vos avis Google, mis en avant sur votre propre site',
        },
      ],
    },
    soluciones: {
      etiqueta: 'Solutions',
      items: [
        {
          clave: 'restauracion',
          titulo: 'Restauration',
          desc: 'Chacun de vos restaurants en course pour la 1re place',
        },
        {
          clave: 'clinicas',
          titulo: 'Cliniques',
          desc: 'Des avis traités avec la prudence qu’exige la santé',
        },
        {
          clave: 'automotriz',
          titulo: 'Automobile',
          desc: 'La confiance qui décide avant de vous confier la voiture',
        },
        {
          clave: 'hoteles',
          titulo: 'Hôtels',
          desc: 'Des avis dans la langue de chaque client, même s’ils arrivent en même temps',
        },
      ],
    },
    multiubicaciones: 'Multi-établissements',
    precios: "Tarifs",
    sobreNosotros: 'À propos',
  },

  contacto: {
    titulo: 'Demandez plus d’informations, ',
    tituloAcento: 'sans engagement',
    cuerpo:
      'Parlez-nous de votre groupe et nous vous expliquons comment cela s’articule avec vos établissements. Nous vous recontactons en moins de 24 heures.',

    nombre: 'Prénom',
    apellidos: 'Nom',
    telefono: 'Téléphone',
    email: 'E-mail professionnel',
    sedes: 'Combien d’établissements avez-vous ?',
    mensaje: 'Message',
    mensajePista: 'Dites-nous ce dont vous avez besoin : combien d’établissements vous avez, ce que vous aimeriez résoudre…',

    politicaA: 'J’ai lu et j’accepte la ',
    politicaEnlace: 'politique de confidentialité',
    politicaB: '.',

    enviar: 'Demander des informations',
    enviando: 'Envoi…',
    pie: 'Nous vous recontactons en moins de 24 heures',

    okTitulo: 'Bien reçu !',
    okCuerpo: 'Nous vous écrivons en moins de 24 heures. En cas d’urgence, appelez-nous au 604 934 869.',

    errNombre: 'Dites-nous votre nom',
    errEmail: 'Il nous faut un e-mail valide pour vous répondre',
    errPolitica: 'Il nous faut votre accord pour conserver ces données et vous répondre',
    errEnvio: 'Nous n’avons pas pu l’envoyer. Réessayez ou appelez-nous au 604 934 869.',
    errThrottle: 'Nous avons déjà reçu votre message. En cas d’urgence, appelez-nous au 604 934 869.',
  },

  precios: {
    titulo: "Un forfait, tout compris",
    subtitulo: "Sans modules ni surprises. Commencez avec 7 jours d'essai.",


    /*
     * El FAQ de la página de precios (no sale en la home).
     *
     * Es lo que hace que `/pricing` tenga algo que indexar: los importes
     * vienen de `/billing/active-pricing` y en el HTML estático la tabla sale
     * vacía, así que sin esto la página serían 250 palabras y ni un número.
     * Aquí son ~400 más, y además le dan el `FAQPage` de schema.org.
     *
     * Lo que afirma está comprobado contra lo que ya dice la app —el
     * prorrateo, la ausencia de permanencia, la tarjeta en el trial, el tope
     * de 10 respuestas— y no inventado para rellenar.
     */
    faqTitulo: "Questions sur les tarifs",
    faq: [
      {
        q: "Que comprend le plan gratuit ?",
        a: "La connexion de votre fiche Google Business, pour toujours et sans abonnement ni carte : tous vos avis dans une seule boîte, la Carte SEO et l'analyse de votre fiche. Vous pouvez répondre à 10 avis par mois avec l'aide de l'IA.",
      },
      {
        q: "Business se facture par compte ou par établissement ?",
        a: "Par établissement actif. Vous pouvez avoir plusieurs établissements sur un même compte et n'activer que ceux que vous voulez ; le sélecteur du tableau vous montre le total avant de souscrire quoi que ce soit.",
      },
      {
        q: "Que se passe-t-il si j'ajoute ou retire un établissement en cours de mois ?",
        a: "La facturation s'ajuste toute seule : la partie non utilisée vous est créditée et le nouveau total au prorata est facturé. Pas besoin d'attendre le renouvellement ni de refaire l'abonnement.",
      },
      {
        q: "Y a-t-il un engagement ?",
        a: "Non. C'est un abonnement mensuel ou annuel que vous pouvez résilier quand vous voulez. En résiliant, vous conservez Business jusqu'à la fin de la période déjà payée.",
      },
      {
        q: "L'essai gratuit demande-t-il une carte ?",
        a: "Oui, pour que le service ne s'interrompe pas à la fin de l'essai. Rien n'est facturé pendant l'essai, et si vous résiliez avant la fin il n'y a aucun frais.",
      },
      {
        q: "Puis-je passer du mensuel à l'annuel ?",
        a: "Oui, quand vous voulez. Le changement s'applique immédiatement et le montant au prorata de la période en cours est prélevé tout de suite.",
      },
    ],
    gratis: {
      nombre: 'Gratuit',
      insignia: 'Sans carte',
      tagline: 'Connectez votre fiche Google Business',
      precio: '0 €',
      nota: 'Pour toujours, sans abonnement',
      cta: 'Commencer gratuitement',
      features: [
        'Tous vos avis Google dans une seule boîte',
        '10 réponses aux avis avec l’IA par mois',
        'Carte SEO : votre position sur Google Maps point par point',
        'Analyse SEO de votre fiche, avec une note et un plan d’action',
      ],
    },

    enterprise: {
      nombre: 'Enterprise',
      insignia: 'Franchises',
      tagline: 'Plus de 30 établissements',
      precio: 'Sur mesure',
      nota: 'Selon le nombre d’établissements',
      cta: 'Contacter les ventes',
      features: [
        'Tout Business sur tous les établissements',
        'Vue d’ensemble et par site',
        'Rapports agrégés pour la direction',
        'Onboarding et support dédiés',
      ],
    },
  },
};

const TRONCOS = { es, en, pt, de, fr };

/**
 * Junta el tronco de un idioma con las cuatro piezas de página.
 *
 * Si a ese idioma le falta ALGUNA pieza devuelve `null`, y `pickLandingV3Content`
 * lo manda entero a español. Es la misma regla que ya estaba escrita («el
 * fallback es por idioma entero y no por clave»), solo que ahora también
 * protege del caso que de verdad duele: añadir un idioma al tronco y olvidarse
 * de un módulo deja `c.producto` a `undefined` y la página en blanco, en ese
 * idioma nada más — o sea, en el que nadie del equipo navega.
 */
function ensambla(lang, tronco) {
  const piezas = {
    home: homeV3[lang],
    producto: productoV3[lang],
    sectores: sectoresV3[lang],
    multiubicaciones: multiubicacionesV3[lang],
    sobreNosotros: sobreNosotrosV3[lang],
  };
  if (Object.values(piezas).some((pieza) => !pieza)) return null;
  return { ...tronco, ...piezas };
}

export const landingV3Content = Object.fromEntries(
  Object.entries(TRONCOS).map(([lang, tronco]) => [lang, ensambla(lang, tronco)]),
);

/**
 * Devuelve el bundle de copy del idioma indicado, con fallback a español.
 *
 * El fallback es por idioma entero y no por clave: un objeto a medio traducir
 * daría una página mitad en un idioma y mitad en otro, que se lee peor que una
 * página entera en español.
 */
export function pickLandingV3Content(language) {
  return landingV3Content[language] || landingV3Content.es;
}
